import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ArrowDown, Bot, Users, Workflow as WorkflowIcon, ShieldCheck } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "@/components/Reveal";
import { Slider } from "@/components/ui/slider";
import { MS_SERVICES, MS_MODEL, MS_PACKAGES, MS_SCENARIOS } from "@/data/managedServices";

const FlowChain = ({ steps, testId }) => (
  <div data-testid={testId} className="flex flex-col items-center gap-1">
    {steps.map((s, i) => (
      <div key={s} className="flex w-full max-w-md flex-col items-center">
        <div className="w-full border border-zinc-900/15 bg-white px-5 py-3 text-center font-mono text-xs uppercase tracking-[0.15em] text-zinc-700">{s}</div>
        {i < steps.length - 1 && <ArrowDown size={14} className="my-1 text-cyan-600" />}
      </div>
    ))}
  </div>
);

const MSROICalculator = () => {
  const navigate = useNavigate();
  const [rate, setRate] = useState(30);
  const [hours, setHours] = useState(15);
  const [people, setPeople] = useState(5);
  const [adminCost, setAdminCost] = useState(5000);

  const currentAnnual = rate * hours * people * 50 + adminCost * 12;
  const automationSavings = Math.round(rate * hours * people * 50 * 0.5);
  const hybridCost = Math.round(currentAnnual * 0.35);
  const annualSavings = Math.max(currentAnnual - hybridCost - automationSavings * 0.2, 0);
  const roi = hybridCost > 0 ? Math.round((annualSavings / hybridCost) * 100) : 0;
  const productivity = Math.round(hours * people * 0.6);
  const f = (n) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });

  const Row = ({ label, value, accent }) => (
    <div className="flex items-baseline justify-between border-b border-zinc-900/10 py-3">
      <span className="text-sm text-zinc-600">{label}</span>
      <span className={`font-heading text-2xl ${accent ? "text-cyan-600" : "text-zinc-900"}`}>{value}</span>
    </div>
  );

  return (
    <section data-testid="ms-roi-calculator" className="border-t border-zinc-900/10 bg-[#E9F4F6] py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <SectionLabel>AI + Human Operations ROI</SectionLabel>
          <h2 className="mt-6 font-heading text-4xl tracking-tight text-zinc-900 sm:text-5xl">What could hybrid operations save you?</h2>
          <div className="mt-10 space-y-7">
            {[
              ["Employee hourly cost", `$${rate}`, rate, setRate, 15, 150, 5],
              ["Hours/week on repetitive work (per person)", `${hours}`, hours, setHours, 1, 40, 1],
              ["Employees doing this work", `${people}`, people, setPeople, 1, 50, 1],
              ["Monthly administrative costs", `$${f(adminCost)}`, adminCost, setAdminCost, 0, 50000, 500],
            ].map(([label, disp, val, set, min, max, step]) => (
              <div key={label}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600">{label}</span>
                  <span className="font-heading text-xl text-cyan-600">{disp}</span>
                </div>
                <Slider value={[val]} onValueChange={(v) => set(v[0])} min={min} max={max} step={step} />
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="border border-cyan-500/30 bg-white p-10">
            <p className="label-mono">Estimated impact</p>
            <div className="mt-6">
              <Row label="Current annual operating cost" value={`$${f(currentAnnual)}`} />
              <Row label="Potential automation savings" value={`$${f(automationSavings)}`} />
              <Row label="Estimated hybrid AI + human cost" value={`$${f(hybridCost)}`} />
              <Row label="Estimated annual savings" value={`$${f(annualSavings)}`} accent />
              <Row label="Estimated ROI" value={`${f(roi)}%`} accent />
              <Row label="Productivity hours regained / week" value={f(productivity)} accent />
            </div>
            <button data-testid="ms-roi-cta" onClick={() => navigate("/assessment")} className="btn-primary mt-8 w-full">
              Get a Detailed ROI Analysis <ArrowUpRight size={16} />
            </button>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Estimates only — actual results depend on your specific business processes.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function ManagedServices() {
  const navigate = useNavigate();
  useEffect(() => { document.title = "Managed AI Services — Augmented Intelligence Systems"; }, []);

  return (
    <main data-testid="managed-services-page" className="pt-32">
      {/* HERO */}
      <section className="grid-bg border-b border-zinc-900/10 pb-20">
        <div className="mx-auto max-w-7xl px-6">
          <SectionLabel>Managed AI Services</SectionLabel>
          <MaskedLines className="mt-6" lines={["AI-Powered Operations.", <span key="a" className="text-cyan-600">Human Expertise When You Need It.</span>]}
            lineClassName="font-heading text-4xl tracking-tighter leading-[1.08] text-zinc-900 sm:text-5xl lg:text-6xl" />
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-zinc-600">
            Automate repetitive work, extend your team, and scale your business with a combination of AI agents, intelligent automation, and trained human specialists. You're buying business outcomes — not hours of labor.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="ms-hero-assessment-btn" onClick={() => navigate("/assessment")} className="btn-primary">Schedule a Free AI Operations Assessment <ArrowUpRight size={16} /></button>
            <a href="#services" className="btn-secondary">Explore Managed Services <ArrowRight size={16} /></a>
          </div>
          <div className="mt-14 flex flex-wrap items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] text-zinc-600">
            {["AI Automation", "Human Specialists", "Business Systems", "Measurable Results"].map((s, i, arr) => (
              <span key={s} className="flex items-center gap-3">
                <span className="border border-cyan-500/40 bg-white px-4 py-2 text-cyan-700">{s}</span>
                {i < arr.length - 1 && <ArrowRight size={14} className="text-cyan-600" />}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* MODEL */}
      <section className="mx-auto max-w-7xl px-6 py-28">
        <Reveal>
          <SectionLabel>The Augmented Intelligence Model</SectionLabel>
          <h2 className="mt-6 font-heading text-4xl tracking-tight text-zinc-900 sm:text-5xl">The Best of AI and Human Intelligence</h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-600">Many business processes shouldn't be completely automated. Our hybrid model puts AI where it excels, people where judgment matters, and automation connecting everything in between.</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {[["AI handles", Bot, MS_MODEL.ai], ["Human specialists handle", Users, MS_MODEL.human], ["Automation connects", WorkflowIcon, MS_MODEL.automation]].map(([title, Icon, items], i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="h-full border border-zinc-900/10 bg-white p-8">
                <Icon size={22} className="text-cyan-600" strokeWidth={1.5} />
                <h3 className="mt-4 font-heading text-xl text-zinc-900">{title}</h3>
                <ul className="mt-4 space-y-2">
                  {items.map((it) => <li key={it} className="flex items-start gap-2 text-sm text-zinc-600"><span className="mt-1.5 h-1 w-1 shrink-0 bg-cyan-500" />{it}</li>)}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}><div className="mt-14"><FlowChain steps={MS_MODEL.flow} testId="ms-model-flow" /></div></Reveal>
      </section>

      {/* SERVICES GRID */}
      <section id="services" className="border-y border-zinc-900/10 bg-[#F0F2F5] py-28">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionLabel>All Managed Services</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl tracking-tight text-zinc-900 sm:text-5xl">Eight ways we run operations with you.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            {MS_SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.05}>
                <Link to={`/managed-services/${s.slug}`} data-testid={`ms-card-${s.slug}`} className={`card-hover group flex h-full flex-col border bg-white p-8 ${s.flagship ? "border-cyan-500/50" : "border-zinc-900/10"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-cyan-600">/{s.num}</span>
                    {s.flagship && <span className="border border-cyan-500/50 bg-cyan-500/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-700">Flagship</span>}
                  </div>
                  <h3 className="mt-6 font-heading text-2xl text-zinc-900">{s.nav}</h3>
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-cyan-600">{s.position}</p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-600">{s.description}</p>
                  <span className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-900 transition-colors group-hover:text-cyan-600">Learn more <ArrowRight size={14} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="mx-auto max-w-7xl px-6 py-28">
        <Reveal>
          <SectionLabel>Service Packages</SectionLabel>
          <h2 className="mt-6 font-heading text-4xl tracking-tight text-zinc-900 sm:text-5xl">Outcomes, packaged.</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {MS_PACKAGES.map((p, i) => (
            <Reveal key={p.tier} delay={i * 0.08}>
              <div data-testid={`ms-package-${p.tier.toLowerCase()}`} className={`flex h-full flex-col border p-8 ${p.featured ? "border-cyan-500 bg-cyan-500/5" : "border-zinc-900/10 bg-white"}`}>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-600">{p.tier}</span>
                <h3 className="mt-3 font-heading text-2xl text-zinc-900">{p.name}</h3>
                <p className="mt-2 text-sm text-zinc-600">{p.who}</p>
                <ul className="mt-6 flex-1 space-y-2.5">
                  {p.items.map((it) => <li key={it} className="flex items-start gap-2 text-sm text-zinc-700"><span className="mt-1.5 h-1 w-1 shrink-0 bg-cyan-500" />{it}</li>)}
                </ul>
                <button onClick={() => navigate("/assessment")} className={`${p.featured ? "btn-primary" : "btn-secondary"} mt-8 w-full`}>{p.cta}</button>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div data-testid="ms-custom-pricing" className="mt-6 flex flex-wrap items-center justify-between gap-6 border border-zinc-900/10 bg-white p-8">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-600">Enterprise & Custom — Custom Pricing</span>
              <p className="mt-3 max-w-2xl text-sm text-zinc-600">Every business has different processes, systems, volume, and automation opportunities. We build the solution around your operation.</p>
            </div>
            <button onClick={() => navigate("/contact")} className="btn-secondary">Request a Custom Proposal</button>
          </div>
        </Reveal>
      </section>

      {/* ROI */}
      <MSROICalculator />

      {/* SCENARIOS */}
      <section className="mx-auto max-w-7xl px-6 py-28">
        <Reveal>
          <SectionLabel>Example Scenarios</SectionLabel>
          <h2 className="mt-6 font-heading text-4xl tracking-tight text-zinc-900 sm:text-5xl">What hybrid operations look like.</h2>
          <p className="mt-4 max-w-xl text-sm text-zinc-500">Illustrative scenarios — not actual client results.</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {MS_SCENARIOS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div data-testid={`ms-scenario-${i}`} className="flex h-full flex-col border border-zinc-900/10 bg-white p-8">
                <span className="w-fit border border-zinc-900/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{c.tag}</span>
                <h3 className="mt-5 font-heading text-xl text-zinc-900">{c.title}</h3>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Before</p>
                <ul className="mt-2 space-y-1.5">{c.before.map((b) => <li key={b} className="text-sm text-zinc-600">— {b}</li>)}</ul>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-600">After</p>
                <ul className="mt-2 space-y-1.5">{c.after.map((a) => <li key={a} className="text-sm text-zinc-700">+ {a}</li>)}</ul>
                <p className="mt-auto pt-6 text-sm italic leading-relaxed text-zinc-600">{c.result}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRUST + SECURITY */}
      <section className="border-t border-zinc-900/10 bg-[#F0F2F5] py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-2">
          <Reveal>
            <SectionLabel>Trust & Transparency</SectionLabel>
            <h2 className="mt-6 font-heading text-3xl tracking-tight text-zinc-900 sm:text-4xl">Human + AI. Not AI Alone.</h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-zinc-600">AI is used strategically — and human specialists remain available whenever judgment, empathy, complexity, or escalation requires it. AI doesn't replace your employees; it allows your team to spend more time on higher-value work.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-zinc-900/10 bg-white p-8">
              <div className="flex items-center gap-3"><ShieldCheck size={20} className="text-cyan-600" /><p className="label-mono">Compliance & Security</p></div>
              <ul className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {["Data security", "Access controls", "Confidentiality", "Least-privilege access", "Workflow auditing", "Human oversight", "Vendor management", "Secure integrations"].map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm text-zinc-600"><span className="mt-1.5 h-1 w-1 shrink-0 bg-cyan-500" />{s}</li>
                ))}
              </ul>
              <p className="mt-5 text-xs leading-relaxed text-zinc-500">For industries involving sensitive information, requirements are evaluated on a case-by-case basis.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-zinc-900/10 py-24">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6">
          <Reveal>
            <h2 className="font-heading text-3xl text-zinc-900 sm:text-4xl">Start with a free assessment.</h2>
            <p className="mt-3 max-w-xl text-zinc-600">Tell us how your operation runs today. We'll show you what to automate, what to augment, and what to leave alone.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <button data-testid="ms-final-assessment-btn" onClick={() => navigate("/assessment")} className="btn-primary">Schedule a Free AI Operations Assessment <ArrowUpRight size={16} /></button>
              <button onClick={() => navigate("/contact")} className="btn-secondary">Request a Demo</button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
